/**
 * Edge designed / Live-complement desks · instrument craft v370.
 * Soft≠Live: complete product surface; Soft invent of KPIs forbidden until Live bus.
 * Never invent device counters, GPS, frames, or sessions.
 *
 * Waves: W12 vision · W5 locate · W3 models · W4 runtime · W8 board (Live)
 *        W10 mesh · W11 store · W9 security · W7 spatial · W6 estate
 */
import { useEffect, useMemo, useState } from "react";
import { html, _Ico, go } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import {
  EdgeDeskHead,
  EdgePlate,
  EdgeMeters,
  EdgeNote,
  EdgeEmpty,
  EdgeActRow,
  EdgeTable,
  EdgeKV,
  EdgeLadder,
  EdgeTiles,
  EdgeDevicePick,
} from "./edgeos-chrome.js";
import { edgeDevicesOf, boardTelemetryViewOf, boardLoopViewOf, edgeWaveHonestyOf, locateDualHonestyOf } from "./edgeos-live.js";
import { ConnectionWatch } from "./connection-watch.js";
import {
  safeBoardPath,
  bindSeat,
  confirmSeat,
  releaseSeat,
  learnSeat,
  pollLearnResult,
} from "./board-api.js";
import { SERVER_ROLES, explainBoardError } from "./board-seat-adapter.js";
import { postDevicePositionSeal } from "./operator-gnss-seal.js";

/** Soft path string for board ops — never throw Unknown operation_id into React. */
function safeBoardEdgePath(op, opts) {
  try {
    return path(op, opts);
  } catch (_e) {
    if (op === "get__devices") {
      const q = (opts && opts.query) || {};
      const lim = q.limit != null ? "?limit=" + encodeURIComponent(String(q.limit)) : "";
      return "/devices" + lim;
    }
    if (op === "get__devices_p") {
      const id = opts && opts.pathParams && opts.pathParams[0];
      return id ? "/devices/" + encodeURIComponent(id) : "";
    }
    return safeBoardPath(op, opts) || "";
  }
}

function useBoardLoopPick(seat) {
  const surveyOn = !!(seat && seat.surveyAllowed);
  const paintMax = (seat && seat.devicePaintMax) || 24;
  const [deviceId, setDeviceId] = useState("");
  const reg = useApi(surveyOn ? safeBoardEdgePath("get__devices", { query: { limit: 80 } }) : null);
  const devices = useMemo(
    () => edgeDevicesOf(reg.data).slice(0, paintMax),
    [reg.data, paintMax],
  );
  useEffect(() => {
    if (deviceId) return;
    const prefer = devices.find((d) => d.online) || devices[0];
    if (prefer && prefer.id) setDeviceId(prefer.id);
  }, [devices, deviceId]);
  const did = String(deviceId || "").trim();
  const loopUrl =
    surveyOn && did
      ? safeBoardPath("get__devices_p_board-loop", { pathParams: [did] }) || null
      : null;
  const loopLive = useApi(loopUrl);
  const loops = useMemo(() => boardLoopViewOf(loopLive.data), [loopLive.data]);
  const facts = useMemo(() => edgeWaveHonestyOf(loops), [loops]);
  return { surveyOn, devices, deviceId, setDeviceId, did, loopLive, loops, facts, reg };
}

function dash(v) {
  return v ? v : "—";
}

export function EstateWaveDesk({ devices, surveyOn, loading, toast, seat }) {
  const list = Array.isArray(devices) ? devices : [];
  const painted = list.slice(0, (seat && seat.devicePaintMax) || 24);
  const aiN = painted.filter((d) =>
    /edge_ai|ai_node|p4/i.test(String(d.kind || d.class || d.label || "")),
  ).length;
  const onlineN = painted.filter((d) => d.online).length;
  const camN = painted.filter((d) =>
    /cam|camera|vision|snap/i.test(String(d.kind || d.class || d.label || d.net || "")),
  ).length;

  return html`<section className="edge-wave edge-wave--estate edge-desk-body" data-craft="v370" data-cap="estate">
    <${EdgeDeskHead}
      title="Nodes"
      wave="W6"
      maturity="live"
      lede="Sole · Edge node rollup. Site plant list and open-one-device lives under Ops Plant."
    />
    <${EdgeMeters}
      ariaLabel="Estate meters"
      items=${[
        { k: "Visible", v: surveyOn ? (loading ? "…" : painted.length) : "—", on: surveyOn && painted.length },
        { k: "Answering", v: surveyOn ? (loading ? "…" : onlineN) : "—", on: surveyOn && onlineN },
        { k: "AI seats", v: surveyOn ? aiN : "—", on: surveyOn && aiN },
        { k: "Camera class", v: surveyOn ? (loading ? "…" : camN) : "—", on: surveyOn && camN },
        { k: "Tier", v: surveyOn && aiN ? "T2 · P4 + IP cam" : (surveyOn ? "T0/T1 unread" : "—"), on: surveyOn && aiN },
      ]}
    />
    <${EdgePlate} title="Node register" meta="Node list · account scope"
      sub="Not Ops Plant · not Skills — only nodes returned for this account">
      ${!surveyOn
        ? html`<${EdgeNote} tone="warn" title="Read seat closed" say="Node register needs an open read seat."/>`
        : null}
      ${!painted.length
        ? html`<${EdgeEmpty}
            title="No devices on this account"
            say="When nodes report, each row shows class · path · camera · AI. Site inventory filters live under Ops Plant."
          />`
        : html`<div className="edge-nodes" role="list">
            ${painted.map(
              (d) => html`<article key=${d.id} className="edge-nodes__card" role="listitem"
                data-on=${d.online ? "1" : "0"}>
                <header>
                  <b>${d.label || d.id}</b>
                  <em data-on=${d.online ? "1" : "0"}>${d.online ? "online" : "quiet"}</em>
                </header>
                <p>${d.id}${d.zone ? " · " + d.zone : ""}${d.net ? " · " + d.net : ""}</p>
                <div className="edge-nodes__tags">
                  <span>class</span>
                  <span>${/cam|camera|vision/i.test(String(d.kind || d.class || d.label || ""))
                    ? "camera class"
                    : "node"}</span>
                  <span>${/edge_ai|ai_node|p4/i.test(String(d.kind || d.class || d.label || ""))
                    ? "AI seat"
                    : "field"}</span>
                </div>
              </article>`,
            )}
          </div>`}
    <//>
    <p className="edge-cap-hand">
      Need plant open / zone filters?
      <button type="button" className="edge-cap-hand__go" onClick=${() => go("/ops/devices")}>Ops Plant</button>
      · hardware live?
      <button type="button" className="edge-cap-hand__go" onClick=${() => go("/machine")}>Machine</button>
      · function packs?
      <button type="button" className="edge-cap-hand__go" onClick=${() => go("/edge/skills")}>Skills · Compose</button>
    </p>
    <${EdgePlate} title="How edge is wired" meta="plan map · not Live KPIs"
      sub="Organism map · plan labels only · not live health meters">
      <${EdgeTiles}
        items=${[
          { k: "Field node", v: "one node · one eye" },
          { k: "Mesh spine", v: "brother pulse events" },
          { k: "Cloud", v: "models · twin · train" },
          { k: "μDock", v: "outbound only" },
        ]}
      />
    <//>
  </section>`;
}

export function VisionWaveDesk({ toast, seat }) {
  const cmd = !!(seat && seat.commandAllowed);
  const pick = useBoardLoopPick(seat);
  const facts = pick.facts;
  return html`<section className="edge-wave edge-desk-body" data-craft="v370">
    <${EdgeDeskHead}
      title="Vision"
      wave="W12"
      maturity=${facts.jpegOk ? "live" : "designed"}
      lede="Sole · one cam path. Digest JPEG is measured. Remux grants stay Tunnel. Soft≠ Vision product."
    />
    <${EdgeDevicePick}
      devices=${pick.devices}
      deviceId=${pick.deviceId}
      setDeviceId=${pick.setDeviceId}
      loading=${pick.reg.loading}
      error=${pick.reg.error}
    />
    <${EdgeNote} tone="info" title="Measured · no invent frames"
      say=${facts.jpegOk
        ? ("board-loop cam=jpeg_ok · remux=" + (facts.w1 || "unread") + " · Digest GET closed. Soft≠ Vision Live · Soft≠ treat JPEG as remux playing.")
        : "No jpeg_ok on this node yet. Open grants only under Tunnel · operator frames under Ops Cams."}/>
    <${EdgeMeters}
      ariaLabel="Vision vs remux"
      items=${[
        { k: "JPEG", v: facts.jpegOk ? "ok" : (facts.cam || "—"), on: facts.jpegOk, em: "board-loop cam" },
        { k: "Remux", v: facts.w1 || "—", on: facts.remuxActive, em: "board-loop w1" },
        { k: "Form A CSI", v: facts.csi || "—", on: facts.csi === "no_sccb", em: "board-loop csi" },
        { k: "Form B", v: facts.jpegOk ? "LAN JPEG" : "—", on: facts.jpegOk },
      ]}
    />
    <p className="edge-cap-hand">
      Open a live stream session?
      <button type="button" className="edge-cap-hand__go" onClick=${() => go("/edge/tunnel")}>Tunnel · Grant</button>
      · see frames?
      <button type="button" className="edge-cap-hand__go" onClick=${() => go("/ops/cameras")}>Ops Cams</button>
    </p>
    <div className="edge-plate-grid">
      <${EdgePlate} title="Camera bind" meta="single source" sub="Prefer JPEG ladder">
        <label className="edge-field">
          <span>Source form</span>
          <select className="inp" disabled=${!cmd} defaultValue="b_jpeg">
            <option value="a_mipi">Form A · MIPI-CSI</option>
            <option value="b_jpeg">Form B · JPEG snapshot (preferred)</option>
            <option value="b_rtsp">Form B · RTSP H.264 baseline</option>
          </select>
        </label>
        <label className="edge-field">
          <span>LAN host</span>
          <input className="inp" placeholder="192.168.x.x" disabled=${!cmd}/>
        </label>
        <label className="edge-field">
          <span>Stream path</span>
          <input className="inp" placeholder="/cam/realmonitor?channel=1" disabled=${!cmd}/>
        </label>
        <label className="edge-field">
          <span>Auth</span>
          <select className="inp" disabled=${!cmd} defaultValue="digest">
            <option value="digest">HTTP Digest</option>
            <option value="basic">Basic (discouraged)</option>
            <option value="relay">Relay re-auth at gateway</option>
          </select>
        </label>
        <${EdgeActRow}>
          <button type="button" className="btn btn--go btn--sm" disabled=${!cmd}
            onClick=${() => toast && toast(
              facts.jpegOk
                ? "Profile already measured jpeg_ok · admit mutate stays Tunnel/Ops"
                : "Camera admit is not armed · no jpeg_ok on board-loop",
              "warn")}>
            Admit profile
          </button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${!cmd}
            onClick=${() => toast && toast(
              facts.cam ? ("Last cam loop · " + facts.cam + " · Soft≠ invent extra probe") : "No cam loop yet",
              "warn")}>
            Probe snapshot
          </button>
        <//>
      <//>
      <${EdgePlate} title="Degrade ladder" meta="precedence" sub="Alarm holds morph · never invent softer fps">
        <${EdgeLadder}
          steps=${[
            { k: "L1", v: "JPEG @1–5 fps · hardware decode", on: facts.jpegOk },
            { k: "L2", v: "RTSP IDR-only soft decode" },
            { k: "L3", v: "RTSP full · lower res/fps" },
            { k: "L4", v: "Abandon vision · sensors/events only" },
          ]}
        />
        <${EdgeKV}
          rows=${[
            { k: "SPS gate", v: "profile_idc==66 constrained" },
            { k: "Color path", v: "Gray path default" },
            { k: "Alarm vs vision", v: "Alarm holds morph" },
          ]}
        />
      <//>
    </div>
    <${EdgePlate} title="Virtual cameras" meta="tunnel names · not local ports"
      sub="Zero inbound ports on ESP">
      <div className="edge-vcams">
        ${["live/raw", "live/ai", "live/object", "live/debug"].map(
          (n) => html`<button key=${n} type="button" className="edge-vcams__chip" disabled
            title="Opens only inside granted μDock session">${n}</button>`,
        )}
      </div>
      <p className="edge-plate__foot">Browser → Gateway → outbound session → stream name.</p>
    <//>
    <${EdgePlate} title="Vision events" meta="ledger">
      <${EdgeTable}
        cols=${["Time", "Path", "Admit", "SPS", "FPS", "Result"]}
        note=${facts.jpegOk
          ? "Digest JPEG measured · remux bytes still Tunnel Soft≠ invent player"
          : "Vision events log — Live when remux + decode report land"}
      />
    <//>
  </section>`;
}

export function LocateWaveDesk({ toast, seat }) {
  const cmd = !!(seat && seat.commandAllowed);
  const pick = useBoardLoopPick(seat);
  const facts = pick.facts;
  const loc = facts.locate || "";
  const { run, busy } = useAction();
  const [gnssBusy, setGnssBusy] = useState(false);
  const devUrl =
    pick.surveyOn && pick.did
      ? safeBoardEdgePath("get__devices_p", { pathParams: [pick.did] }) || null
      : null;
  const devLive = useApi(devUrl);
  const dual = useMemo(() => locateDualHonestyOf(facts, devLive.data), [facts, devLive.data]);
  const sealFromPhone = async () => {
    if (!cmd || !pick.did) {
      toast && toast("Command seat closed or no device", "warn");
      return;
    }
    setGnssBusy(true);
    try {
      await postDevicePositionSeal({ run, pathFn: path, deviceId: pick.did, toast });
      pick.loopLive && pick.loopLive.reload && pick.loopLive.reload();
      devLive.reload && devLive.reload();
    } finally {
      setGnssBusy(false);
    }
  };
  return html`<section className="edge-wave edge-desk-body" data-craft="v370">
    <${EdgeDeskHead}
      title="Locate"
      wave="W5"
      maturity=${facts.locateSealed ? "live" : "designed"}
      lede="Installer phone GNSS seals the board · LAN fingerprint is which site net, not metres. Never cell / GeoIP. Never invent GPS on P4."
    />
    <${EdgeDevicePick}
      devices=${pick.devices}
      deviceId=${pick.deviceId}
      setDeviceId=${pick.setDeviceId}
      loading=${pick.reg.loading}
      error=${pick.reg.error}
    />
    <${EdgeNote} tone=${dual.split ? "warn" : "info"} title=${dual.split ? "Two locate facts" : "Honesty first"}
      say=${dual.say
        + " · GNSS " + (facts.gps || "unread")
        + " · FTM " + (facts.ftm || "unread")
        + " · LAN " + (facts.lanPath || "unread") + " identity Soft≠ metres Soft≠ GeoIP"
        + " · Soft≠ cell/GeoIP · Soft≠ invent GPS · Soft≠ FTM field until C6 ECO2 + seal."}/>
    <div className="edge-plate-grid">
      <${EdgePlate} title="Report" meta="device → server seal">
        <${EdgeKV}
          rows=${[
            { k: "Loop", v: dash(loc) },
            { k: "Sources", v: "phone GNSS install seal · FTM residual · UART GNSS optional" },
            { k: "GNSS", v: facts.gps === "absent"
              ? "absent · no chip on this SKU · Soft≠ guess"
              : dash(facts.gps) },
            { k: "C6", v: dash(facts.c6) },
            { k: "C6 fw", v: dash(facts.c6fw) },
            { k: "FTM", v: facts.ftm === "unprobed"
              ? "unprobed · C6 ECO1 has no initiator · Soft≠ field"
              : dash(facts.ftm) },
            { k: "LAN path", v: facts.lanPath
              ? (facts.lanPath + " · identity, not metres")
              : "unread" },
            { k: "LAN prefix", v: facts.lanPrefix || "—" },
            { k: "BSSID", v: facts.lanBssid || "—" },
            { k: "Gateway MAC", v: facts.lanGwMac || "—" },
            { k: "LAN lock", v: facts.lanLock === "drift"
              ? "drift · board moved Soft≠ clear seal Soft≠ metres"
              : (facts.lanLock || "—") },
            { k: "Neighbors", v: facts.lanNbrN ? (facts.lanNbrN + " ARP MACs · not metres") : "—" },
            { k: "LLDP / CDP", v: ((facts.lanLldp || "unread") + " / " + (facts.lanCdp || "unread"))
              + (facts.lanLldpWho ? (" · " + facts.lanLldpWho + " Soft≠ metres") : "")
              + (facts.lanCdpWho ? (" · CDP " + facts.lanCdpWho) : "") },
            { k: "Inherited brother", v: dual.registry && String(dual.registry).toUpperCase() === "L2_BROTHER"
              ? (dual.say || "same-LAN brother · not metres")
              : "—" },
          ]}
        />
        <label className="edge-field">
          <span>Survey point</span>
          <input className="inp" placeholder="North yard stake A" disabled=${!cmd}/>
        </label>
        <${EdgeActRow}>
          <button type="button" className="btn btn--go btn--sm" disabled=${!cmd || busy || gnssBusy}
            onClick=${sealFromPhone}>
            ${gnssBusy ? "Reading phone GNSS…" : "Seal from this phone GNSS"}
          </button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${!cmd}
            onClick=${() => toast && toast(
              loc ? ("Already reporting locate=" + loc + " · period emit after MQTT") : "No locate loop to force",
              "warn")}>
            Force report now
          </button>
        <//>
      <//>
      <${EdgePlate} title="Last fix" meta="no invent">
        <${EdgeKV}
          rows=${[
            { k: "Lat / Lng", v: dual.hasCoords
              ? (String(dual.lat) + " / " + String(dual.lng) + " · registry only")
              : (facts.locateSealed ? "sealed · coords on position ingest" : "— · no invent GPS") },
            { k: "Accuracy", v: "phone GNSS ≤ 50 m gate · cell/LBS refused" },
            { k: "Edge", v: dash(dual.edge || loc) },
            { k: "Registry", v: dash(dual.registry) },
            { k: "Age", v: loc ? "board-loop 1 Hz" : "—" },
            { k: "Trust", v: dual.split ? "SPLIT" : (facts.locateSealed ? "sealed" : (facts.locateHold ? "HOLD" : dash(loc))) },
          ]}
        />
      <//>
    </div>
    <${EdgePlate} title="Locate ledger" meta="Live with position ingest">
      <${EdgeTable}
        cols=${["TS", "Lat", "Lng", "Acc m", "Source", "Admitted"]}
        note=${loc
          ? ("Locate loop " + loc + " · admit ledger fills on position ingest Soft≠ invent pin")
          : "Locate admit ledger with device position ingest"}
      />
    <//>
  </section>`;
}

export function ModelsWaveDesk({ toast, seat }) {
  const cmd = !!(seat && seat.commandAllowed);
  const pick = useBoardLoopPick(seat);
  const facts = pick.facts;
  return html`<section className="edge-wave edge-desk-body" data-craft="v370">
    <${EdgeDeskHead}
      title="Models"
      wave="W3"
      maturity="designed"
      lede="Capsule life cell · ESP observes and proposes only — never scores personnel or drives actuators."
    />
    <${EdgeDevicePick}
      devices=${pick.devices}
      deviceId=${pick.deviceId}
      setDeviceId=${pick.setDeviceId}
      loading=${pick.reg.loading}
      error=${pick.reg.error}
    />
    <${EdgeNote} tone="warn" title="W3 parked"
      say=${"board-loop w3=" + dash(facts.w3) + " · ai_parked=" + (facts.aiParked ? "true" : "false") + " · owner model · Soft≠ Vision Live · Soft≠ cold-load product"}/>
    <${EdgePlate} title="Genome contract" meta="§5.3 parked · no custom IR">
      <${EdgeKV}
        rows=${[
          { k: "Token Genome", v: "parked · owner model track" },
          { k: "Living State", v: "INT16 + decay · not loaded" },
          { k: "Life Cell", v: "observe · update · propose only" },
          { k: "CNIR", v: "ONNX → .espdl + SUIT · Soft≠ invent IR" },
          { k: "μA1–μA4", v: "not claimed · Soft≠ μA1 Live" },
        ]}
      />
    <//>
    <div className="edge-plate-grid">
      <${EdgePlate} title="Active life cell" meta="cmo candidate">
        <${EdgeKV}
          rows=${[
            { k: "Input", v: "320×320 GRAY8" },
            { k: "State", v: "INT16 living slots + decay" },
            { k: "Rights", v: "observe · update · propose only" },
            { k: "Teacher", v: "Local schedule · not cloud train on path" },
          ]}
        />
        <label className="edge-field">
          <span>Capsule id</span>
          <input className="inp" placeholder="cmo.v0.slot_a" disabled=${!cmd}/>
        </label>
        <${EdgeActRow}>
          <button type="button" className="btn btn--go btn--sm" disabled=${!cmd}
            onClick=${() => toast && toast("w3=parked · cold load stays owner-model track Soft≠ invent Live", "warn")}>
            Cold load → PSRAM
          </button>
          <button type="button" className="btn btn--ghost btn--sm" disabled=${!cmd}
            onClick=${() => toast && toast("Capsule revoke is not armed yet pending", "warn")}>
            Revoke
          </button>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => go("/edge/skills")}>
            Skills warehouse
          </button>
        <//>
      <//>
      <${EdgePlate} title="Token Genome" meta="versioned contract">
        <ul className="edge-bullets">
          <li>entity · space · motion · state · time</li>
          <li>signal · resource · goal</li>
          <li>line format = distill target = compat latch</li>
        </ul>
        <${EdgeKV}
          rows=${[
            { k: "Package", v: "ONNX → .espdl + SUIT + CBOR" },
            { k: "Refuse", v: "invent mAP · unpigned weights" },
          ]}
        />
      <//>
    </div>
    <${EdgePlate} title="Model inventory" meta="TF cold store + flash cache">
      <${EdgeTable}
        cols=${["Capsule", "Genome", "State", "Signed", "Slot", "Resident"]}
        note=${facts.tfMounted
          ? "TF mounted · model slots stay empty until owner capsule Soft≠ invent resident"
          : "Model inventory from TF cold store + flash cache reports"}
      />
    <//>
  </section>`;
}

export function RuntimeWaveDesk({ toast, seat }) {
  const cmd = !!(seat && seat.commandAllowed);
  const pick = useBoardLoopPick(seat);
  const facts = pick.facts;
  return html`<section className="edge-wave edge-desk-body" data-craft="v370">
    <${EdgeDeskHead}
      title="Runtime"
      wave="W4"
      maturity=${facts.psram || facts.w4 ? "live" : "designed"}
      lede="Tensor pool · DMA · dual-core. Joint budget with Vision — no invent peak numbers."
    />
    <${EdgeDevicePick}
      devices=${pick.devices}
      deviceId=${pick.deviceId}
      setDeviceId=${pick.setDeviceId}
      loading=${pick.reg.loading}
      error=${pick.reg.error}
    />
    <${EdgeMeters}
      items=${[
        { k: "W4 loop", v: dash(facts.w4) },
        { k: "PSRAM", v: dash(facts.psram) },
        { k: "Cmd", v: dash(facts.cmd) },
        { k: "Flash", v: dash(facts.flash) },
        { k: "HOT lease", v: "—" },
        { k: "ΔT", v: "— · no soak" },
      ]}
    />
    <${EdgePlate} title="W4 five soak" meta="unread · Soft≠ invent">
      <${EdgeKV}
        rows=${[
          { k: "1 Layer cache", v: "unread · param_copy vs XIP" },
          { k: "2 Tensor pool", v: "unread · peak with decode+net" },
          { k: "3 Scheduler", v: "unread · dual-core vs W12" },
          { k: "4 DMA", v: "unread · cam/SD/ETH PSRAM" },
          { k: "5 .espdl load", v: "unread · flash vs SD + sign" },
        ]}
      />
    <//>
    <${EdgePlate} title="Lease board" meta="FrameLease · TensorLease">
      <${EdgeTable}
        cols=${["Lane", "Holder", "Bytes", "TTL", "State"]}
        note=${facts.w4 || facts.psram
          ? ("Measured " + [facts.w4, facts.psram].filter(Boolean).join(" · ") + " · leases still empty Soft≠ invent KiB")
          : "Runtime will paint leases when W4 report bus Live"}
      />
      <${EdgeActRow} cls="mt">
        <button type="button" className="btn btn--ghost btn--sm" disabled=${!cmd}
          onClick=${() => toast && toast("Force reclaim is not armed yet pending", "warn")}>
          Reclaim expired leases
        </button>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${!cmd}
          onClick=${() => toast && toast("Memory profile is not armed yet pending", "warn")}>
          Snapshot profile_memory
        </button>
      <//>
    <//>
    <${EdgePlate} title="Dual-core roles" meta="isolation first">
      <${EdgeTiles}
        items=${[
          { k: "Core A", v: "Decode / network" },
          { k: "Core B", v: "Infer / state update" },
          { k: "Shared", v: "PSRAM bandwidth gate" },
          { k: "Refuse", v: "alarm-path malloc" },
        ]}
      />
    <//>
  </section>`;
}

function seatRowLabel(s) {
  if (!s) return "—";
  const id = String(s.seat_id || "").trim() || "—";
  const lab = String(s.label || "").trim();
  return lab ? id + " · " + lab : id;
}

function bindingView(s) {
  const b = (s && s.binding) || null;
  if (!b || typeof b !== "object") {
    return { role: null, state: "unassigned", version: null, confirms: [], need: 0 };
  }
  return {
    role: b.role || null,
    state: String(b.state || "claimed"),
    version: b.version != null ? Number(b.version) : null,
    confirms: Array.isArray(b.confirmed_by) ? b.confirmed_by : [],
    need: Number(b.confirms_required) || 1,
  };
}

/**
 * Edge Board W8 (no Soft toast theatre).
 * Read: pins + telemetry · Write: learn / bind / confirm / release (BE ACL + dual quorum).
 */
export function BoardWaveDesk({ toast, seat }) {
  const surveyOn = !!(seat && seat.surveyAllowed);
  const cmd = !!(seat && seat.commandAllowed);
  const paintMax = (seat && seat.devicePaintMax) || 24;
  const [deviceId, setDeviceId] = useState("");
  const [selSeat, setSelSeat] = useState("");
  const [role, setRole] = useState("pir");
  const [reason, setReason] = useState("Edge Board · wiring matches this seat");
  const [busy, setBusy] = useState(false);
  const [wireOk, setWireOk] = useState(false);

  const reg = useApi(surveyOn ? safeBoardEdgePath("get__devices", { query: { limit: 80 } }) : null);
  const devices = useMemo(() => edgeDevicesOf(reg.data).slice(0, paintMax), [reg.data, paintMax]);
  useEffect(() => {
    if (deviceId) return;
    const prefer = devices.find((d) => d.online) || devices[0];
    if (prefer && prefer.id) setDeviceId(prefer.id);
  }, [devices, deviceId]);
  const did = String(deviceId || "").trim();

  const pinsUrl =
    surveyOn && did ? safeBoardEdgePath("get__devices_p_pins", { pathParams: [did] }) : null;
  const teleUrl =
    surveyOn && did
      ? safeBoardEdgePath("get__devices_p_board-telemetry", { pathParams: [did] })
      : null;
  const pinsLive = useApi(pinsUrl);
  const teleLive = useApi(teleUrl);
  const loopUrl =
    surveyOn && did
      ? safeBoardPath("get__devices_p_board-loop", { pathParams: [did] }) || null
      : null;
  const loopLive = useApi(loopUrl);
  const boardFacts = useMemo(
    () => edgeWaveHonestyOf(boardLoopViewOf(loopLive.data)),
    [loopLive.data],
  );
  const tele = useMemo(() => boardTelemetryViewOf(teleLive.data), [teleLive.data]);

  const seats = useMemo(() => {
    const raw = (pinsLive.data && (pinsLive.data.items || pinsLive.data.seats)) || [];
    return Array.isArray(raw) ? raw : [];
  }, [pinsLive.data]);
  const pinsErr = pinsLive.error ? explainBoardError(pinsLive.error) : null;
  const teleErr = teleLive.error ? explainBoardError(teleLive.error) : null;

  const claimedN = seats.filter((s) => {
    const v = bindingView(s);
    return v.state === "claimed" || v.state === "pending_confirm";
  }).length;
  const verifiedN = seats.filter((s) => bindingView(s).state === "verified").length;
  const mappedN = seats.filter((s) => s.gpio != null).length;
  const pinmap = !!(pinsLive.data && pinsLive.data.pinmap_reported);

  const selected = seats.find((s) => String(s.seat_id || "") === String(selSeat || "")) || null;
  const selB = bindingView(selected);

  const reloadBoard = () => {
    pinsLive.reload && pinsLive.reload();
    teleLive.reload && teleLive.reload();
    loopLive.reload && loopLive.reload();
  };

  const act = async (fn, okMsg) => {
    if (!surveyOn) {
      toast && toast("Survey seat sealed — cannot read seats", "warn");
      return;
    }
    if (!did) {
      toast && toast("Pick a node first", "warn");
      return;
    }
    if (!cmd) {
      toast && toast("Command seat sealed — writes refuse on the server", "warn");
      return;
    }
    setBusy(true);
    try {
      const out = await fn();
      if (!out || !out.ok) {
        toast && toast((out && out.error) || "Board write refused", "bad");
        return;
      }
      toast && toast(okMsg, "go");
      reloadBoard();
    } finally {
      setBusy(false);
    }
  };

  const onLearn = () => {
    const seatId = String(selSeat || "").trim().toUpperCase();
    if (!/^HDR-\d+$/i.test(seatId)) {
      toast && toast("Pick an HDR seat to learn — bus power seats cannot learn", "warn");
      return;
    }
    if (!surveyOn || !did) {
      toast && toast("Pick a node first", "warn");
      return;
    }
    if (!cmd) {
      toast && toast("Command seat sealed — writes refuse on the server", "warn");
      return;
    }
    act(async () => {
      const started = await learnSeat(did, seatId, { window_s: 20 });
      if (!started.ok) return started;
      toast && toast("Learn open · short header seat to GND on the pad", "warn");
      const polled = await pollLearnResult(did);
      if (!polled.ok) return polled;
      return { ok: true };
    }, "Learn finished · re-check the seat table");
  };

  const onBind = () => {
    if (!wireOk) {
      toast && toast("Confirm the physical wire matches before claim", "warn");
      return;
    }
    const seatId = String(selSeat || "").trim().toUpperCase();
    if (!seatId) {
      toast && toast("Pick a seat", "warn");
      return;
    }
    const why = String(reason || "").trim();
    if (why.length < 8) {
      toast && toast("Reason must be at least 8 characters", "warn");
      return;
    }
    act(
      () => bindSeat(did, seatId, { role, function_label: "", operator_reason: why }),
      "Seat claimed · needs confirm before hardware drive",
    );
  };

  const onConfirm = () => {
    const seatId = String(selSeat || "").trim().toUpperCase();
    if (!seatId) {
      toast && toast("Pick a seat", "warn");
      return;
    }
    if (selB.version == null) {
      toast && toast("Seat has no claim yet — bind first", "warn");
      return;
    }
    const why = String(reason || "").trim();
    if (why.length < 8) {
      toast && toast("Reason must be at least 8 characters", "warn");
      return;
    }
    act(
      () =>
        confirmSeat(did, seatId, {
          expected_version: selB.version,
          operator_reason: why,
        }),
      "Confirm recorded · second operator if quorum still open",
    );
  };

  const onRelease = () => {
    const seatId = String(selSeat || "").trim().toUpperCase();
    if (!seatId) {
      toast && toast("Pick a seat", "warn");
      return;
    }
    act(() => releaseSeat(did, seatId), "Seat released");
  };

  const rows = seats.map((s) => {
    const b = bindingView(s);
    const conf =
      b.need > 0
        ? String(b.confirms.length) + "/" + String(b.need)
        : "—";
    return {
      id: s.seat_id,
      cells: [
        seatRowLabel(s),
        s.gpio != null ? String(s.gpio) : "—",
        b.role || "—",
        b.state,
        conf,
      ],
    };
  });

  return html`<section className="edge-wave edge-desk-body" data-craft="v498"
    data-cmc-page="page148-edge-board" data-soft-api-path-paint-forbidden="1">
    <${EdgeDeskHead}
      title="Board"
      wave="W8"
      maturity="live"
      lede="Live seats · learn · claim · dual-human confirm. Server never invents GPIO or drives unconfirmed execute seats."
    />
    <${EdgeNote} tone="info" title="Execute class stays gated"
      say="Relay and siren need operator claim plus enough distinct confirms on the server. A local checkbox alone never drives hardware."/>
    ${!surveyOn
      ? html`<${EdgeNote} tone="warn" title="Survey seat sealed"
          say="Seat list stays empty until survey is open. Nothing is invented."/>`
      : null}

    <${EdgeDevicePick}
      devices=${devices}
      deviceId=${did}
      setDeviceId=${(id) => { setDeviceId(id); setSelSeat(""); }}
      loading=${reg.loading}
      error=${reg.error}
    />

    <${EdgeMeters}
      ariaLabel="Board meters"
      items=${[
        { k: "Seats", v: !did ? "—" : (pinsLive.loading ? "…" : seats.length), on: !!seats.length },
        { k: "Claimed", v: !did ? "—" : claimedN, on: claimedN > 0 },
        { k: "Verified", v: !did ? "—" : verifiedN, on: verifiedN > 0 },
        { k: "Pin map", v: !did ? "—" : (pinmap ? "reported" : "waiting"), on: pinmap },
        { k: "I2C", v: !did ? "—" : dash(boardFacts.i2c), on: boardFacts.i2c === "scanned" },
        { k: "SPK", v: !did ? "—" : dash(boardFacts.spk), on: boardFacts.spk === "i2s_ready" },
      ]}
    />
    <${EdgePlate} title="Closed-loop predicates" meta="§3.1 · Soft≠ skip to verified">
      <${EdgeKV}
        rows=${[
          { k: "1 Present", v: !did ? "—" : String(seats.length) + " seats" },
          { k: "2 Mapped", v: !did ? "—" : String(mappedN) + " gpio reported" },
          { k: "3 Classed", v: boardFacts.i2c === "scanned" ? "I2C silicon · header waiting" : dash(boardFacts.i2c) },
          { k: "4 Bound", v: !did ? "—" : String(claimedN) },
          { k: "5 Verified", v: !did ? "—" : String(verifiedN) },
          { k: "6 Admitted", v: "— · no execute cmd this slice" },
          { k: "7 Proved", v: "open · FW-PIN-FIELD NO/NC" },
        ]}
      />
    <//>

    <div className="edge-plate-grid">
      <${EdgePlate} title="Header seats" meta="Live board seats"
        sub="Pick a row, then claim or confirm. Unconfirmed GPIO never appears."
        acts=${html`<button type="button" className="btn btn--ghost btn--sm"
          disabled=${!did || pinsLive.loading || !surveyOn}
          onClick=${() => { reloadBoard(); toast && toast("Board re-read", "go"); }}>
          Re-read
        </button>`}>
        ${pinsErr
          ? html`<${EdgeNote} tone="warn" title="Seats not ready on this node"
              say=${pinsErr}/>`
          : null}
        <${EdgeTable}
          cols=${["Seat", "Pad", "Role", "State", "Confirms"]}
          rows=${rows}
          note=${!did
            ? "Pick a node to load seats"
            : pinsLive.loading
              ? "Reading Live seats…"
              : "No seats yet · wait for board surface or device pin map"}
        />
        ${seats.length
          ? html`<label className="edge-field mt">
              <span>Working seat</span>
              <select className="inp" value=${selSeat}
                disabled=${busy}
                onChange=${(e) => setSelSeat(e.target.value)}>
                <option value="">Select…</option>
                ${seats.map((s) => html`
                  <option key=${s.seat_id} value=${s.seat_id}>
                    ${seatRowLabel(s)}${s.assignable === false ? " · reserved" : ""}
                  </option>`)}
              </select>
            </label>`
          : null}
      <//>

      <${EdgePlate} title="Wire & confirm" meta="authority on server"
        sub="Claim is admin-gated. Confirm may be a second pair of eyes. Quorum lives only on the server.">
        <label className="edge-field">
          <span>Function</span>
          <select className="inp" value=${role} disabled=${busy || !cmd}
            onChange=${(e) => setRole(e.target.value)}>
            ${SERVER_ROLES.map((r) => html`
              <option key=${r.id} value=${r.id}>
                ${r.label}${r.risk === "high" ? " · execute high-risk" : ""}
              </option>`)}
          </select>
        </label>
        <label className="edge-field">
          <span>Why (audit)</span>
          <input className="inp" value=${reason} disabled=${busy || !cmd}
            onInput=${(e) => setReason(e.target.value)}
            placeholder="At least 8 characters · who wired this"/>
        </label>
        <label className="edge-check">
          <input type="checkbox" checked=${wireOk} disabled=${busy || !cmd}
            onChange=${(e) => setWireOk(!!e.target.checked)}/>
          Physical wiring matches the function I am about to claim
        </label>
        <${EdgeActRow} cls="mt">
          <button type="button" className="btn btn--go btn--sm"
            disabled=${busy || !cmd || !did || !selSeat}
            onClick=${onLearn}>Start learn</button>
          <button type="button" className="btn btn--sm"
            disabled=${busy || !cmd || !did || !selSeat}
            onClick=${onBind}>Claim seat</button>
          <button type="button" className="btn btn--sm"
            disabled=${busy || !cmd || !did || !selSeat || selB.version == null}
            onClick=${onConfirm}>Confirm</button>
          <button type="button" className="btn btn--ghost btn--sm"
            disabled=${busy || !cmd || !did || !selSeat}
            onClick=${onRelease}>Release</button>
        <//>
        ${selected
          ? html`<${EdgeKV}
              rows=${[
                { k: "Seat", v: seatRowLabel(selected) },
                { k: "Assignable", v: selected.assignable === false ? "no" : "yes" },
                { k: "Pad", v: selected.gpio != null ? String(selected.gpio) : "waiting for pin map / learn" },
                { k: "State", v: selB.state },
                {
                  k: "Confirms",
                  v:
                    selB.need > 0
                      ? String(selB.confirms.length) + " of " + String(selB.need) +
                        (selB.confirms.length ? " · " + selB.confirms.join(", ") : "")
                      : "—",
                },
              ]}
            />`
          : null}
      <//>
    </div>

    <${EdgePlate} title="Board health" meta="fresh tele only"
      sub="Temperatures and heap only when the node reports Live telemetry — empty until then.">
      ${teleErr
        ? html`<${EdgeNote} tone="info" title="Telemetry not on this node"
            say=${teleErr}/>`
        : tele
          ? html`<${EdgeKV}
              rows=${[
                { k: "Profile", v: tele.profile },
                { k: "Temp °C", v: tele.temp != null ? String(tele.temp) : "—" },
                { k: "Heap free", v: tele.heap != null ? String(tele.heap) : "—" },
                { k: "Uptime s", v: tele.uptime != null ? String(tele.uptime) : "—" },
                { k: "I2C", v: dash(boardFacts.i2c) },
                { k: "SPK", v: dash(boardFacts.spk) },
                { k: "MIC", v: dash(boardFacts.mic) },
                { k: "RTC", v: dash(boardFacts.rtc) },
                { k: "USB", v: dash(boardFacts.usb) },
                { k: "DSI", v: dash(boardFacts.dsi) },
                { k: "CSI", v: dash(boardFacts.csi) },
                { k: "Watch", v: dash(boardFacts.watch) },
              ]}
            />`
          : html`<${EdgeEmpty}
              title=${!did ? "Pick a node" : "No telemetry loaded"}
              say="Seats can still load. Telemetry fills after the node reports a board frame."
            />`}
    <//>

    ${did && surveyOn
      ? html`<${ConnectionWatch} deviceId=${did} boardLive=${true} toast=${toast}
          onBoardReload=${reloadBoard}/>`
      : null}
  </section>`;
}

export function MeshWaveDesk({ toast, seat }) {
  const cmd = !!(seat && seat.commandAllowed);
  const pick = useBoardLoopPick(seat);
  const facts = pick.facts;
  return html`<section className="edge-wave edge-desk-body" data-craft="v370">
    <${EdgeDeskHead}
      title="Brothers"
      wave="W10"
      maturity="designed"
      lede="Cloud fan-out stays Live on Borrow. Mesh pulse for offline. A1–A6 frozen. Soft≠ invent RF hops."
    />
    <${EdgeDevicePick}
      devices=${pick.devices}
      deviceId=${pick.deviceId}
      setDeviceId=${pick.setDeviceId}
      loading=${pick.reg.loading}
      error=${pick.reg.error}
    />
    <${EdgeNote} tone=${facts.meshLiveClaim ? "warn" : "info"} title="Mesh honesty"
      say=${facts.meshLiveClaim
        ? "Server sent mesh live — treat as suspect Soft≠ claim_mesh_live"
        : ("board-loop mesh=" + dash(facts.mesh) + " · c6=" + dash(facts.c6) + " · Soft≠ invent RF")}/>
    <div className="edge-plate-grid">
      <${EdgePlate} title="Cloud layer" meta="siblings on Borrow">
        <p className="edge-plate__p">Roster / group fan-out already on device siblings. This desk owns mesh rules + pulse mirror.</p>
        <${EdgeActRow}>
          <button type="button" className="btn btn--ghost btn--sm"
            onClick=${() => {
              try { go("/edge/share"); } catch { /* */ }
              toast && toast("Borrow desk", "go");
            }}>
            Open Borrow
          </button>
        <//>
      <//>
      <${EdgePlate} title="Mesh layer" meta="events only">
        <${EdgeKV}
          rows=${[
            { k: "Allowed", v: "event codes · priming · identity hash" },
            { k: "Forbidden", v: "model weights · images on mesh" },
            { k: "Arbitration", v: "A1–A6 frozen · RF field not live Soft≠ rewrite" },
            { k: "A1", v: "Cloud fan-out is semantic authority" },
            { k: "A2", v: "fanout_nonce ≥120s dedupe" },
            { k: "A3", v: "tenant_hash + group_hash or silent drop" },
            { k: "A4", v: "events · priming · id hash only" },
            { k: "A5", v: "later obeys earlier nonce" },
            { k: "A6", v: "cross-tenant / group mismatch refuse" },
          ]}
        />
        <button type="button" className="btn btn--sm" disabled=${!cmd}
          onClick=${() => toast && toast("Pulse policy is not armed yet pending", "warn")}>
          Edit pulse policy
        </button>
      <//>
    </div>
    <${EdgePlate} title="Pulse ledger">
      <${EdgeTable}
        cols=${["From", "To", "Kind", "Signed", "Tenant", "Result"]}
        note=${facts.mesh
          ? ("mesh=" + facts.mesh + " · pulse ledger stays empty until RF field Soft≠ invent hop")
          : "Pulse ledger with mesh reflect"}
      />
    <//>
  </section>`;
}

export function StoreWaveDesk({ toast, seat }) {
  const cmd = !!(seat && seat.commandAllowed);
  const pick = useBoardLoopPick(seat);
  const facts = pick.facts;
  const cardUrl =
    pick.surveyOn && pick.did
      ? safeBoardPath("get__devices_p_storage_card", { pathParams: [pick.did] }) || null
      : null;
  const cardLive = useApi(cardUrl);
  const card = cardLive.data && typeof cardLive.data === "object" ? cardLive.data : {};
  const cap = card.capacity_mb != null ? String(card.capacity_mb) : "—";
  const free = card.free_mb != null ? String(card.free_mb) : "—";
  const fs = card.fs ? String(card.fs) : "—";
  return html`<section className="edge-wave edge-desk-body" data-craft="v370">
    <${EdgeDeskHead}
      title="Store"
      wave="W11"
      maturity=${facts.tfMounted ? "live" : "designed"}
      lede="Cold archive only — never on the hot infer path. Active model caches to flash."
    />
    <${EdgeDevicePick}
      devices=${pick.devices}
      deviceId=${pick.deviceId}
      setDeviceId=${pick.setDeviceId}
      loading=${pick.reg.loading}
      error=${pick.reg.error}
    />
    <${EdgeMeters}
      items=${[
        { k: "TF loop", v: dash(facts.tf) },
        { k: "FS", v: fs },
        { k: "Capacity", v: cap === "—" ? "—" : cap + " MiB" },
        { k: "Free", v: free === "—" ? "—" : free + " MiB" },
      ]}
    />
    <${EdgePlate} title="Slots" meta="signature required">
      <${EdgeTable}
        cols=${["Slot", "Capsule", "Bytes", "Signed", "Health", "Action"]}
        note=${facts.tfMounted
          ? ("TF " + facts.tf + " · " + fs + " · Soft≠ auto-format · model slots empty")
          : "TF inventory after SDMMC report Live"}
      />
      <${EdgeActRow} cls="mt">
        <button type="button" className="btn btn--go btn--sm" disabled=${!cmd}
          onClick=${() => toast && toast("Cold promote is not armed yet pending", "warn")}>
          Promote cold → flash
        </button>
        <button type="button" className="btn btn--ghost btn--sm" disabled=${!cmd}
          onClick=${() => toast && toast("Model rollback is not armed yet pending", "warn")}>
          Rollback previous
        </button>
      <//>
    <//>
    <${EdgePlate} title="Iron rules" meta="cold path">
      <ul className="edge-bullets">
        <li>No operator “swap mid-frame” path</li>
        <li>FAT32 only · no LittleFS-on-SD</li>
        <li>Evidence ring + wear ledger · no plain cam passwords</li>
        <li>Mesh Cache = PSRAM at runtime · TF only on change Soft≠ infer from the card</li>
      </ul>
    <//>
  </section>`;
}

export function SecurityWaveDesk({ toast, seat }) {
  const cmd = !!(seat && seat.commandAllowed);
  const pick = useBoardLoopPick(seat);
  const facts = pick.facts;
  return html`<section className="edge-wave edge-desk-body" data-craft="v370">
    <${EdgeDeskHead}
      title="Secure"
      wave="W9"
      maturity=${facts.floor || facts.ota || facts.mtls ? "live" : "designed"}
      lede="Identity · outbound-only · Capsule sign · Secure Boot RSA-3072. Soft≠ ECDSA SB on P4 ≤v3.2 (AR2026-006)."
    />
    <${EdgeDevicePick}
      devices=${pick.devices}
      deviceId=${pick.deviceId}
      setDeviceId=${pick.setDeviceId}
      loading=${pick.reg.loading}
      error=${pick.reg.error}
    />
    <div className="edge-plate-grid">
      <${EdgePlate} title="Identity" meta="cert / KMU">
        <${EdgeKV}
          rows=${[
            { k: "Id", v: dash(facts.id) },
            { k: "Sync", v: dash(facts.sync) },
            { k: "MQTT", v: dash(facts.mqtt) },
            { k: "mTLS", v: dash(facts.mtls) },
            { k: "Cmd", v: dash(facts.cmd) },
            { k: "UART", v: dash(facts.uart) },
            { k: "Inbound ports", v: "FORBIDDEN (all)" },
          ]}
        />
        <button type="button" className="btn btn--ghost btn--sm" disabled=${!cmd}
          onClick=${() => toast && toast("Cert rotate is not armed yet pending", "warn")}>
          Rotate cert
        </button>
      <//>
      <${EdgePlate} title="Boot & storage" meta="production gates">
        <${EdgeKV}
          rows=${[
            { k: "Secure Boot", v: facts.w9 || facts.floor || "—" },
            { k: "Floor", v: dash(facts.floor) },
            { k: "P4 rev", v: dash(facts.p4rev) },
            { k: "ECDSA SB", v: (facts.p4rev === "v13" || facts.p4rev === "v31" || facts.p4rev === "v32")
              ? "AR2026-006 V1.1 · P4 ≤v3.2 unsafe · RSA-3072 only"
              : (facts.p4rev ? "confirm rev · Soft≠ ECDSA SB" : "—") },
            { k: "Flash size", v: dash(facts.flash) },
            { k: "Flash encrypt", v: dash(facts.fe) },
            { k: "OTA", v: dash(facts.ota) },
            { k: "Watch", v: dash(facts.watch) },
            { k: "C6 fw", v: dash(facts.c6fw) },
          ]}
        />
      <//>
    </div>
    <${EdgePlate} title="Security audit">
      <${EdgeTable}
        cols=${["TS", "Surface", "Event", "Actor", "Result"]}
        note="Security audit stream with Capsule / session denies"
      />
    <//>
  </section>`;
}

export function SpatialWaveDesk() {
  return html`<section className="edge-wave edge-desk-body" data-craft="v370" data-deferred="1">
    <${EdgeDeskHead}
      title="Space"
      wave="W7"
      maturity="deferred"
      lede="Spatial identity expansion frozen until owner picks direction. Nav stays complete."
    />
    <${EdgeNote} tone="warn" title="Owner decision pending"
      say="SoT §5.7 暂缓. No pretend desks here. When direction freezes, this surface maps Twin assets ↔ Edge seats."/>
    <${EdgePlate} title="Reserved lanes">
      <ul className="edge-bullets">
        <li>Site volume bindings beyond Ops volume pads</li>
        <li>Asset twin ↔ edge_ai_node seating</li>
        <li>Cross-site spatial query (L2 only)</li>
      </ul>
      <${EdgeEmpty}
        title="Hold · no surface yet"
        say="Spatial maps stay empty until product freezes the plan — nothing invented here."
      />
    <//>
  </section>`;
}
